import { useState } from 'react';
import { getCurrentAnnotationId } from '../../lib/url';
import { useAnnotation, useDocumentBreadcrumb } from './index';
import { useSplitConfirmNavigation } from './session/useSplitConfirmNavigation';
import { useSplitSession } from './session/useSplitSession';
import { SplitScreenReady } from './SplitScreenReady';
import { SplitScreenLoading, SplitScreenMessage } from './SplitScreenStatus';

/**
 * Frame of the new splitting screen: top bar, resizable sidebar and the
 * splitting canvas, wired to the split-session façade (MAT-1959) and the
 * confirm / cancel / leave flow (MAT-1965).
 */
export const SplitScreen = ({
  annotationId,
  // Test-only seam: nothing sets the conflict state yet (see TODO below), so
  // this is how the state and its rendering are exercised in isolation until
  // the real 409 trigger lands.
  initialConflict = 'idle',
}: {
  annotationId: number;
  initialConflict?: 'idle' | 'conflict';
}) => {
  // TODO(MAT-19xx): the real conflict trigger lands with the start-edit-mode
  // network call (startEditPages returning 409); nothing sets this yet.
  // oxlint-disable-next-line react-ext/hook-use-state -- no setter yet, see TODO above
  const [conflict] = useState<'idle' | 'conflict'>(initialConflict);

  const { data, isLoading, isError } = useAnnotation(annotationId);

  const { workspaceName, queueName } = useDocumentBreadcrumb(data?.annotation);

  const { onConfirmSuccess, onConfirmError } = useSplitConfirmNavigation();

  const { config, confirm, confirmInProgress } = useSplitSession({
    annotationId,
    // Read lazily: resolved during unmount, see `useCancelEditMode`.
    getTargetAnnotationId: () =>
      getCurrentAnnotationId(window.location.pathname),
    onConfirmSuccess,
  });

  if (conflict === 'conflict') {
    return (
      <SplitScreenMessage
        messageId="features.documentSplit.conflict.title"
        dataCy="split-screen-conflict"
      />
    );
  }

  if (isError) {
    return (
      <SplitScreenMessage
        messageId="features.documentSplit.notFound.title"
        dataCy="split-screen-not-found"
      />
    );
  }

  if (isLoading || !config) {
    return <SplitScreenLoading />;
  }

  return (
    <SplitScreenReady
      key={config.annotationUrl}
      config={config}
      document={data.document}
      pages={data.pages}
      workspaceName={workspaceName}
      queueName={queueName}
      confirm={confirm}
      confirmInProgress={confirmInProgress}
      onConfirmError={onConfirmError}
    />
  );
};
